import React from 'react';
import { cn } from '../../lib/utils';
import { TrendingUp, TrendingDown } from 'lucide-react';

/**
 * SquareCard - Universal square KPI card component for URGAA
 * Clean, modern design with consistent aspect ratios
 */
const SquareCard = ({ 
  title, 
  value, 
  subtitle,
  icon: Icon, 
  trend,
  trendDirection = 'up',
  onClick,
  className,
  variant = 'default', // 'default' | 'dark' | 'summary' | 'gradient'
  size = 'md', // 'sm' | 'md' | 'lg'
  children
}) => {
  const sizeClasses = {
    sm: 'p-3 min-h-[100px]',
    md: 'p-4 min-h-[120px]',
    lg: 'p-5 min-h-[140px]'
  };

  const variantClasses = {
    default: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white',
    dark: 'bg-gray-900 text-white border border-gray-800',
    summary: 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white border border-gray-700',
    gradient: 'bg-gradient-to-br from-blue-600 to-purple-600 text-white border-0'
  };

  const TrendIcon = trendDirection === 'up' ? TrendingUp : TrendingDown;

  return (
    <div
      onClick={onClick}
      className={cn(
        'rounded-xl shadow-sm transition-all duration-200',
        'flex flex-col justify-between aspect-square',
        'hover:shadow-lg hover:scale-[1.02]',
        onClick && 'cursor-pointer',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
    >
      {/* Icon Header */}
      {Icon && (
        <div className="flex items-center justify-between mb-2">
          <div className={cn(
            'p-2 rounded-lg',
            variant === 'dark' || variant === 'summary' || variant === 'gradient'
              ? 'bg-white/10' 
              : 'bg-blue-50 dark:bg-blue-900/30'
          )}>
            <Icon size={16} className={
              variant === 'dark' || variant === 'summary' || variant === 'gradient'
                ? 'text-white' 
                : 'text-blue-600 dark:text-blue-400'
            } />
          </div>
          {trend && (
            <div className={cn(
              'flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded',
              trendDirection === 'up' 
                ? 'text-green-600 bg-green-50 dark:text-green-400 dark:bg-green-900/30' 
                : 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30'
            )}>
              <TrendIcon size={12} />
              <span>{trend}</span>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col justify-center flex-1">
        {title && (
          <p className={cn(
            'text-xs font-medium mb-1 truncate',
            variant === 'dark' || variant === 'summary' || variant === 'gradient'
              ? 'text-gray-300' 
              : 'text-gray-500 dark:text-gray-400'
          )}>
            {title}
          </p>
        )}
        {value && (
          <p className={cn(
            'text-xl md:text-2xl font-bold truncate',
            variant === 'dark' || variant === 'summary' || variant === 'gradient'
              ? 'text-white' 
              : 'text-gray-900 dark:text-white'
          )}>
            {value}
          </p>
        )}
        {subtitle && (
          <p className={cn(
            'text-[10px] mt-1 truncate',
            variant === 'dark' || variant === 'summary' || variant === 'gradient'
              ? 'text-gray-400' 
              : 'text-gray-500 dark:text-gray-400'
          )}>
            {subtitle}
          </p>
        )}
        {children}
      </div>
    </div>
  );
};

export default SquareCard;
